import { FeatureURI, CategoryID } from '@sbt-web/network/types';
import { roomType, room, multiLevel, mileage, furnished, floor, bathrooms } from './formatters.js';

const FeatureConfigurations = {
    [FeatureURI.Bathrooms]: { placeholder: 'Bagni', formatter: bathrooms },
    [FeatureURI.CarType]: { placeholder: 'Tipologia' },
    [FeatureURI.Floor]: { placeholder: 'Piano', formatter: floor },
    [FeatureURI.Fuel]: { placeholder: 'Carburante' },
    [FeatureURI.Furnished]: { placeholder: 'Arredata', formatter: furnished },
    [FeatureURI.Gearbox]: { placeholder: 'Cambio' },
    [FeatureURI.Mileage]: { placeholder: 'km', formatter: mileage },
    [FeatureURI.MultiLevel]: { placeholder: 'Piano', formatter: multiLevel },
    [FeatureURI.Pollution]: { placeholder: 'Emissioni' },
    [FeatureURI.RegisterDate]: { placeholder: 'Mese/Anno' },
    [FeatureURI.Room]: { placeholder: 'Locali', formatter: room },
    [FeatureURI.RoomType]: { formatter: roomType },
    [FeatureURI.Size]: { placeholder: 'mq' },
    [FeatureURI.VehicleStatus]: { placeholder: 'Tipologia' },
    [FeatureURI.Year]: { placeholder: 'Anno' },
    [FeatureURI.ItemCondition]: { placeholder: 'Condizione' },
    [FeatureURI.ComputerType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.AudiovideoType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.PhoneType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.ClothingType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.ChildrenType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.BookType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.SportType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.BicycleType]: {
        placeholder: 'Tipologia',
    },
    [FeatureURI.HobbyType]: {
        placeholder: 'Tipologia',
    },
};
const MainDataFeatures = {
    // MOTORS
    [CategoryID.Auto]: [
        FeatureURI.VehicleStatus,
        FeatureURI.RegisterDate,
        FeatureURI.Mileage,
        FeatureURI.Fuel,
        FeatureURI.Gearbox,
        FeatureURI.Pollution,
        FeatureURI.CarType,
    ],
    [CategoryID.Moto]: [
        FeatureURI.VehicleStatus,
        FeatureURI.RegisterDate,
        FeatureURI.Mileage,
        FeatureURI.MotorbicycleType,
        FeatureURI.CubicCapacity,
    ],
    // REAL ESTATE
    [CategoryID.Appartamenti]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.Ville]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.MultiLevel,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.UfficiLocali]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.CaseVacanza]: [
        FeatureURI.Size,
        FeatureURI.Room,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.CamerePostiLetto]: [
        FeatureURI.RoomType,
        FeatureURI.Furnished,
        FeatureURI.Floor,
        FeatureURI.Bathrooms,
    ],
    [CategoryID.TerreniRustici]: [FeatureURI.Size],
    [CategoryID.LoftMansarde]: [FeatureURI.Size, FeatureURI.Room],
    [CategoryID.GarageBox]: [FeatureURI.Size],
    // Casa & Persona
    [CategoryID.AbbigliamentoAccessori]: [
        FeatureURI.ClothingGender,
        FeatureURI.ClothingType,
    ],
    // Sports & Hobby
    [CategoryID.Biciclette]: [FeatureURI.BicycleType, FeatureURI.ItemCondition],
    // Lavoro
    [CategoryID.OfferteLavoro]: [
        FeatureURI.JobCategory,
        FeatureURI.WorkLevel,
        FeatureURI.WorkHours,
    ],
};
const itemConditionsFormattedValues = [
    'Nuovo',
    'Come nuovo',
    'Ottimo',
    'Buono',
    'Danneggiato',
];
/**
 * Filters and formats the main features of an item for display in item cards and details.
 *
 * This function selects a set of main features for the given item based on its category,
 * formats their values (using custom formatters if available), and returns an array of
 * objects containing the label, placeholder, and icon URL for each feature.
 *
 * @param item - The item whose features are to be formatted.
 * @param config - Optional configuration object:
 *   - `short`?: boolean - If true, returns a short version of the label.
 *   - `assetsBase`?: string - Base URL for static assets (used for icon URLs).
 *   - `maxFeatures`?: number - Maximum number of features to include.
 * @returns An array of `ItemMainFeature` objects, each containing:
 *   - `label`: The formatted label for the feature.
 *   - `placeholder`: The placeholder text for the feature (if any).
 *   - `iconURL`: The URL to the feature's icon.
 *
 * @example
 * ```ts
 * const mainData = formatMainData(item, { assetsBase: 'https://static.example.com', maxFeatures: 3 });
 * // mainData = [
 * //   { label: '120.000 km', placeholder: '', iconURL: 'https://static.example.com/static/icons/features/mileage.svg' },
 * //   ...
 * // ]
 * ```
 */
function formatMainData(item, config = {}) {
    if (!item?.features) {
        return [];
    }
    let featureKeys = MainDataFeatures[item.category?.id];
    if (!featureKeys) {
        return [];
    }
    // Limit the max number of features to show if specified in config
    if (config.maxFeatures != null) {
        featureKeys = featureKeys.slice(0, config.maxFeatures);
    }
    // For Auto and Moto categories, use MileageScalar if available
    if (item.category.id === CategoryID.Auto ||
        item.category.id === CategoryID.Moto) {
        if (item.features[FeatureURI.MileageScalar]) {
            featureKeys = featureKeys.map((feat) => {
                return feat === FeatureURI.Mileage ? FeatureURI.MileageScalar : feat;
            });
        }
    }
    return featureKeys.map((key) => {
        let label = '';
        const feature = item.features[key];
        const featureConfig = FeatureConfigurations[key];
        if (key === FeatureURI.ItemCondition) {
            // For ItemCondition, match the value to a known formatted value
            itemConditionsFormattedValues.find((itemConditionsFormattedValue) => {
                if (feature !== undefined &&
                    feature?.values?.[0]?.value.indexOf(itemConditionsFormattedValue) !==
                        -1) {
                    label = itemConditionsFormattedValue;
                }
                return label;
            });
        }
        else if (feature?.values?.[0]?.value) {
            // Use custom formatter if available, otherwise use the raw value
            label =
                featureConfig?.formatter?.(feature, config) ??
                    feature?.values?.[0].value;
        }
        return {
            label,
            placeholder: featureConfig?.placeholder ?? '',
            iconURL: `${config.assetsBase || ''}/static/icons/features${key}.svg`,
        };
    });
}

export { formatMainData };
//# sourceMappingURL=formatMainData.js.map
